import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, Box, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch,
  FormControlLabel, IconButton, Tooltip, Card, CardContent, Stack
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { domainsApi } from '../api';

function Domains() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [checkResult, setCheckResult] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    domain: '',
    targetUrl: '',
    pathPrefix: '',
    stripPrefix: false,
    sslEnabled: false,
    isActive: true,
    description: ''
  });

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const res = await domainsApi.list();
      setRoutes(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch domain routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRoutes(); }, []);

  const resetForm = () => {
    setForm({
      domain: '',
      targetUrl: '',
      pathPrefix: '',
      stripPrefix: false,
      sslEnabled: false,
      isActive: true,
      description: ''
    });
    setEditRoute(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (route) => {
    setEditRoute(route);
    setForm({
      domain: route.domain,
      targetUrl: route.targetUrl,
      pathPrefix: route.pathPrefix || '',
      stripPrefix: route.stripPrefix,
      sslEnabled: route.sslEnabled,
      isActive: route.isActive,
      description: route.description || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      if (editRoute) {
        await domainsApi.update(editRoute.id, form);
        setSuccess(`Domain route for '${form.domain}' updated`);
      } else {
        await domainsApi.create(form);
        setSuccess(`Domain route for '${form.domain}' created`);
      }
      setDialogOpen(false);
      resetForm();
      fetchRoutes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save domain route');
    }
  };

  const handleDelete = async (route) => {
    try {
      await domainsApi.delete(route.id);
      setSuccess(`Domain route for '${route.domain}' deleted`);
      setDeleteConfirm(null);
      fetchRoutes();
    } catch {
      setError('Failed to delete domain route');
    }
  };

  const handleToggle = async (route) => {
    try {
      await domainsApi.toggle(route.id);
      setSuccess(`Domain route '${route.domain}' ${route.isActive ? 'disabled' : 'enabled'}`);
      fetchRoutes();
    } catch {
      setError('Failed to toggle domain route');
    }
  };

  const handleCheck = async (route) => {
    try {
      setError('');
      const res = await domainsApi.check(route.id);
      setCheckResult(res.data);
    } catch {
      setError('Failed to check connectivity');
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  const activeCount = routes.filter(r => r.isActive).length;
  const sslCount = routes.filter(r => r.sslEnabled).length;

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LanguageIcon sx={{ fontSize: 36, mr: 1, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>Domain Routing</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate} sx={{ mr: 1 }}>
          Add Domain
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={fetchRoutes}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Summary cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h4" color="primary">{routes.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total Routes</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h4" color="success.main">{activeCount}</Typography>
            <Typography variant="body2" color="text.secondary">Active</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h4" color="warning.main">{routes.length - activeCount}</Typography>
            <Typography variant="body2" color="text.secondary">Inactive</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140 }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="h4" color="info.main">{sslCount}</Typography>
            <Typography variant="body2" color="text.secondary">SSL Enabled</Typography>
          </CardContent>
        </Card>
      </Stack>

      {routes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LanguageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No domain routes configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a domain route to forward traffic from a domain to a service running in the swarm.
          </Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={handleOpenCreate}>
            Add Your First Domain
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Target URL</TableCell>
                <TableCell>Path Prefix</TableCell>
                <TableCell>SSL</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {routes.map((route) => (
                <TableRow key={route.id} sx={{ opacity: route.isActive ? 1 : 0.5 }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="bold">{route.domain}</Typography>
                      <Tooltip title="Open in browser">
                        <IconButton
                          size="small"
                          href={`${route.sslEnabled ? 'https' : 'http'}://${route.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{route.targetUrl}</Typography>
                  </TableCell>
                  <TableCell>
                    {route.pathPrefix ? (
                      <Chip label={route.pathPrefix} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                    {route.stripPrefix && route.pathPrefix && (
                      <Chip label="strip" size="small" color="info" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={route.sslEnabled ? 'HTTPS' : 'HTTP'}
                      color={route.sslEnabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={route.isActive ? 'Active' : 'Inactive'}
                      color={route.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {route.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Check connectivity">
                      <IconButton size="small" color="info" onClick={() => handleCheck(route)}>
                        <HealthAndSafetyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={route.isActive ? 'Disable' : 'Enable'}>
                      <IconButton size="small" onClick={() => handleToggle(route)} color={route.isActive ? 'warning' : 'success'}>
                        <PowerSettingsNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenEdit(route)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(route)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRoute ? 'Edit Domain Route' : 'Add Domain Route'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Domain"
            fullWidth
            placeholder="e.g., example.com or *.example.com"
            value={form.domain}
            onChange={(e) => setForm({ ...form, domain: e.target.value })}
            helperText="The domain name to route. Use *.domain.com for wildcard subdomains."
          />
          <TextField
            margin="dense"
            label="Target URL"
            fullWidth
            placeholder="e.g., http://my_service:80"
            value={form.targetUrl}
            onChange={(e) => setForm({ ...form, targetUrl: e.target.value })}
            helperText="The internal URL of the service (Docker service name and port)."
          />
          <TextField
            margin="dense"
            label="Path Prefix (optional)"
            fullWidth
            placeholder="e.g., /app"
            value={form.pathPrefix}
            onChange={(e) => setForm({ ...form, pathPrefix: e.target.value })}
            helperText="Only route requests matching this path prefix."
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.stripPrefix} onChange={(e) => setForm({ ...form, stripPrefix: e.target.checked })} />}
              label="Strip path prefix before forwarding"
            />
            <FormControlLabel
              control={<Switch checked={form.sslEnabled} onChange={(e) => setForm({ ...form, sslEnabled: e.target.checked })} />}
              label="SSL/HTTPS enabled for this domain"
            />
            <FormControlLabel
              control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Route is active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!form.domain || !form.targetUrl}
          >
            {editRoute ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}

      {/* Connectivity Check Result Dialog */}
      <Dialog open={!!checkResult} onClose={() => setCheckResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Connectivity Check</DialogTitle>
        <DialogContent>
          {checkResult && (
            <Box>
              <Typography variant="body2" gutterBottom><strong>Domain:</strong> {checkResult.domain}</Typography>
              <Typography variant="body2" gutterBottom><strong>Target:</strong> <code>{checkResult.target}</code></Typography>
              <Alert severity={checkResult.reachable ? 'success' : 'error'} sx={{ mt: 1 }}>
                {checkResult.reachable
                  ? `Backend reachable — responded with HTTP ${checkResult.statusCode} (${checkResult.status})`
                  : `Backend unreachable — ${checkResult.error}`}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Domain Route</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the route for <strong>{deleteConfirm?.domain}</strong>?
            Traffic to this domain will no longer be forwarded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button onClick={() => handleDelete(deleteConfirm)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Domains;
